import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import { AxiosRequestConfigMethods } from "@/api/types";
import {
  BasePaginationTasksResponse,
  TasksResponse,
  UpdateTaskRequest,
  AddTaskRequest,
  UserAnalyticsResponse,
  SondorRank,
  UserSubordinatesResponse,
  GetUserSubordinatesParams,
} from "@/modules/tasks/dtos/index";
import { EnvironmentVariables } from "@/modules/common/types";
import { BasePaginationParams } from "@/modules/common/dtos/index.v2";
import { JourneysResponse } from "@/modules/leads/dtos/index.v2";
import { PropertiesResponse } from "@/modules/tasks/dtos/index";
import {
  GetSondorRanksParams,
  GetUserAnalyticsParams,
} from "@/modules/tasks/types";

export const getTasksDashboard = async ({
  filters,
  pagination,
}: {
  filters: any;
  pagination: BasePaginationParams;
}) => {
  const apiUrl = brandUrl<BasePaginationTasksResponse<TasksResponse>>(
    `${EnvironmentVariables.TASKS_API_URL}/tasks`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...pagination,
        ...filters,
      },
    },
  });
};

export const getTaskStats = async (statsFilters?: any) => {
  const apiUrl = brandUrl<TasksResponse>(
    `${EnvironmentVariables.TASKS_API_URL}/tasks/analytica/`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: statsFilters ? { ...statsFilters } : {},
    },
  });
};

export const updateTask = async ({
  updateTaskRequest,
  id,
}: {
  updateTaskRequest: UpdateTaskRequest;
  id: number | string;
}) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.TASKS_API_URL}/tasks/${id}/done`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { ...updateTaskRequest },
    },
  });
};

export const getTasksCount = async (filters?: any) => {
  const apiUrl = brandUrl<TasksResponse>(
    `${EnvironmentVariables.TASKS_API_URL}/tasks/analytica-count-by-due-date/`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: filters ? { ...filters } : {},
    },
  });
};

export const createTask = async (taskData: AddTaskRequest) => {
  const apiUrl = brandUrl(`${EnvironmentVariables.TASKS_API_URL}/tasks`);
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      includeMultipleOrganizations: false,
      method: AxiosRequestConfigMethods.POST,
      data: { ...taskData },
    },
  });
};

export const getLeadsJourneysUserHierarchy = async (
  searchTerm?: string,
  userUId?: string
): Promise<JourneysResponse> => {
  const apiUrl = brandUrl<JourneysResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/journeys/users/hierarchy`
  );

  const params: Record<string, string> = {};
  if (searchTerm) params.search = searchTerm;
  if (userUId) params.userUId = userUId;

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  });
};

export const getProperties = async (
  searchTerm?: string,
  userUId?: string
): Promise<PropertiesResponse> => {
  const apiUrl = brandUrl<PropertiesResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/property-tracks`
  );

  const params: Record<string, string> = {};
  if (searchTerm) params.search = searchTerm;
  if (userUId) params.userUId = userUId;

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  });
};

export const getUserSubordinates = async (
  params?: GetUserSubordinatesParams
) => {
  const apiUrl = brandUrl<UserSubordinatesResponse>(
    `${EnvironmentVariables.TASKS_API_URL}/users`
  );

  const { data } = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      includeMultipleOrganizations: false,
      params,
    },
  });

  return data;
};

export const getUserAnalytics = async (params?: GetUserAnalyticsParams) => {
  const apiUrl = brandUrl<UserAnalyticsResponse>(
    `${EnvironmentVariables.TASKS_API_URL}/tasks/analytica-count-by-user`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      includeMultipleOrganizations: false,
      params: {
        onlyU: false,
        ...params,
      },
    },
  });
};

export const getRanks = async (params?: GetSondorRanksParams) => {
  const apiUrl = brandUrl<SondorRank[]>(
    `${EnvironmentVariables.TASKS_API_URL}/control-plain/ranks`
  );

  const ranksResponse = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.GET,
      isEncoded: false,
      includeMultipleOrganizations: false,
      params,
    },
  });

  return ranksResponse.data || [];
};

export const deleteTask = async (uid: number | string) => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.TASKS_API_URL}/tasks/archive`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { taskUIds: [uid] },
    },
  });
};
